/**
 * Orchestrates prediction caching for the hook:
 * - Reads local cache to prime UI instantly.
 * - Persists new/cached predictions with a TTL.
 * - Designed to swap cache impl (local -> remote) without touching hook logic.
 */

import { LocalPredictionCache } from "../cache/LocalPredictionCache";

export default class CachedPredictionService {
  constructor(cacheImpl = null, { ttlMs = 60 * 60 * 1000 } = {}) {
    this.cache = cacheImpl || new LocalPredictionCache(ttlMs);
    this.ttlMs = ttlMs;
  }

  buildKeys({ gameIds, sport, userId }) {
    return (gameIds || []).map((gameId) => ({
      userId: userId || 'anon',
      sport: sport.toUpperCase(),
      gameId: String(gameId)
    }));
  }

  /**
   * Prime UI from local cache.
   * @returns object keyed by gameId with prediction payloads.
   */
  async primeFromCache({ gameIds, sport, userId }) {
    if (!userId || !gameIds?.length) return {};
    const keys = this.buildKeys({ gameIds, sport, userId });
    return this.cache.getMany(keys);
  }

  /**
   * Persist predictions (fresh or server-cached) locally.
   * @param {Object} predictionsByGameId
   * @param {Object} options { sport, userId, expiresAtMs? }
   */
  async saveToCache(predictionsByGameId, { sport, userId, expiresAtMs } = {}) {
    if (!userId || !predictionsByGameId) return;
    const now = Date.now();
    const entries = Object.entries(predictionsByGameId).map(([gameId, prediction]) => ({
      userId,
      sport: sport.toUpperCase(),
      gameId: String(gameId),
      prediction,
      expiresAt: expiresAtMs || (now + this.ttlMs)
    }));
    await this.cache.putMany(entries);
  }
}
